/**
 * DealDocumentsTab
 *
 * Renders the Documents tab on the Deal Detail page.
 * - Groups deal documents by applicant (name + role)
 * - Shows a progress bar: (received + verified + linked) / total
 * - Filter dropdown by collection status
 * - Per-group table with status badges and action buttons
 * - Delete confirmation via AlertDialog
 * - Add/Edit document via DocumentUploadModal (F005)
 * - Generate Checklist via GenerateChecklistModal (F006/F007)
 * - Quick status transitions per row (F008)
 * - Checkbox multi-select + bulk action toolbar (F008)
 * - Rejection flow with inline reason input (F008)
 * - Expiry indicators (amber/red) with tooltips (F008)
 *
 * AC001: Tab renders document list
 * AC002: Progress bar shows completion
 * AC003: Applicant grouping
 * AC004: Status badges with colours
 * AC005: Filter by status
 * AC006: Action buttons (View, Edit, Delete with confirmation)
 * AC007: Quick status transitions (F008)
 * AC008: Bulk select + bulk actions (F008)
 * AC009: Rejection with reason (F008)
 * AC010: Generate Checklist button wired (F008)
 * AC011: Expiry indicators (F008)
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Eye,
  Edit,
  Trash2,
  Plus,
  FileText,
  Loader2,
  AlertTriangle,
  AlertCircle,
  X,
  CheckSquare,
  ListChecks,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format, isBefore, addDays } from "date-fns";
import { DocumentUploadModal } from "@/components/documents/document-upload-modal";
import { GenerateChecklistModal } from "@/components/documents/generate-checklist-modal";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DocumentCollectionStatus =
  | "outstanding"
  | "requested"
  | "received"
  | "verified"
  | "rejected"
  | "linked";

export interface DealDocument {
  id: string;
  dealId: string;
  documentTypeId: string | null;
  documentTypeName: string;
  documentCategoryName: string | null;
  accountId: string | null;
  contactId: string | null;
  applicantName: string | null;
  applicantRole: string;
  collectionStatus: DocumentCollectionStatus;
  requestedAt: string | null;
  receivedAt: string | null;
  verifiedAt?: string | null;
  rejectionReason?: string | null;
  fileName: string | null;
  fileUrl: string | null;
  documentDate: string | null;
  expiryDate: string | null;
  linkedEntityDocumentId: string | null;
  notes: string | null;
}

// ---------------------------------------------------------------------------
// Status badge helpers
// ---------------------------------------------------------------------------

const STATUS_LABELS: Record<DocumentCollectionStatus, string> = {
  outstanding: "Outstanding",
  requested: "Requested",
  received: "Received",
  verified: "Verified",
  rejected: "Rejected",
  linked: "Linked",
};

function getStatusBadge(status: DocumentCollectionStatus) {
  switch (status) {
    case "outstanding":
      return (
        <Badge
          variant="secondary"
          data-testid={`status-badge-${status}`}
        >
          Outstanding
        </Badge>
      );
    case "requested":
      return (
        <Badge
          variant="outline"
          className="border-amber-400 text-amber-600 bg-amber-50"
          data-testid={`status-badge-${status}`}
        >
          Requested
        </Badge>
      );
    case "received":
      return (
        <Badge
          className="bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100"
          variant="outline"
          data-testid={`status-badge-${status}`}
        >
          Received
        </Badge>
      );
    case "verified":
      return (
        <Badge
          className="bg-green-100 text-green-700 border-green-200 hover:bg-green-100"
          variant="outline"
          data-testid={`status-badge-${status}`}
        >
          Verified
        </Badge>
      );
    case "rejected":
      return (
        <Badge
          variant="destructive"
          data-testid={`status-badge-${status}`}
        >
          Rejected
        </Badge>
      );
    case "linked":
      return (
        <Badge
          className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100"
          variant="outline"
          data-testid={`status-badge-${status}`}
        >
          Linked
        </Badge>
      );
    default:
      return (
        <Badge variant="secondary" data-testid="status-badge-unknown">
          {status}
        </Badge>
      );
  }
}

// ---------------------------------------------------------------------------
// Expiry indicator helper
// ---------------------------------------------------------------------------

function ExpiryIndicator({ expiryDate }: { expiryDate: string | null }) {
  if (!expiryDate) return null;

  const expiry = new Date(expiryDate);
  const now = new Date();
  const thirtyDaysFromNow = addDays(now, 30);

  if (isBefore(expiry, now)) {
    // Past expiry — red
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertCircle
              className="h-4 w-4 text-red-500 flex-shrink-0"
              data-testid="expiry-expired-icon"
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>Expired {format(expiry, "d MMM yyyy")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  if (isBefore(expiry, thirtyDaysFromNow)) {
    // Expiring within 30 days — amber
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertTriangle
              className="h-4 w-4 text-amber-500 flex-shrink-0"
              data-testid="expiry-warning-icon"
            />
          </TooltipTrigger>
          <TooltipContent>
            <p>Expiring {format(expiry, "d MMM yyyy")}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Grouping helper
// ---------------------------------------------------------------------------

interface ApplicantGroup {
  key: string;
  applicantName: string | null;
  applicantRole: string;
  documents: DealDocument[];
}

function groupByApplicant(documents: DealDocument[]): ApplicantGroup[] {
  const map = new Map<string, ApplicantGroup>();

  for (const doc of documents) {
    // Use accountId+contactId+role as the group key; fall back to name+role
    const key = `${doc.accountId ?? ""}|${doc.contactId ?? ""}|${doc.applicantRole}`;
    if (!map.has(key)) {
      map.set(key, {
        key,
        applicantName: doc.applicantName,
        applicantRole: doc.applicantRole,
        documents: [],
      });
    }
    map.get(key)!.documents.push(doc);
  }

  return Array.from(map.values());
}

// ---------------------------------------------------------------------------
// Filter options
// ---------------------------------------------------------------------------

const FILTER_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "all", label: "All Statuses" },
  { value: "outstanding", label: "Outstanding" },
  { value: "requested", label: "Requested" },
  { value: "received", label: "Received" },
  { value: "verified", label: "Verified" },
  { value: "rejected", label: "Rejected" },
];

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DealDocumentsTabProps {
  dealId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DealDocumentsTab({ dealId }: DealDocumentsTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ---------------------------------------------------------------------------
  // Local state
  // ---------------------------------------------------------------------------
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [deleteDocId, setDeleteDocId] = useState<string | null>(null);
  const [deleteDocName, setDeleteDocName] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<DealDocument | null>(null);

  // F008: Generate Checklist modal
  const [generateModalOpen, setGenerateModalOpen] = useState(false);

  // F008: Checkbox multi-select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // F008: Rejection flow — track which doc is being rejected + reason text
  const [rejectingDocId, setRejectingDocId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>("");

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  const { data: documents = [], isLoading } = useQuery<DealDocument[]>({
    queryKey: ["/api/crm/deals", dealId, "documents"],
    enabled: !!dealId,
  });

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const deleteMutation = useMutation({
    mutationFn: (docId: string) =>
      apiRequest("DELETE", `/api/crm/deal-documents/${docId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/deals", dealId, "documents"],
      });
      toast({ title: "Document deleted", description: "Document removed from checklist." });
      setDeleteDialogOpen(false);
      setDeleteDocId(null);
    },
    onError: () => {
      toast({
        title: "Delete failed",
        description: "Could not delete the document. Please try again.",
        variant: "destructive",
      });
    },
  });

  // F008: Single status transition mutation
  const statusMutation = useMutation({
    mutationFn: ({
      docId,
      status,
      extra,
    }: {
      docId: string;
      status: string;
      extra?: Record<string, unknown>;
    }) =>
      apiRequest("PATCH", `/api/crm/deal-documents/${docId}`, {
        collectionStatus: status,
        ...extra,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/deals", dealId, "documents"],
      });
      const label = STATUS_LABELS[variables.status as DocumentCollectionStatus] ?? variables.status;
      toast({
        title: "Status updated",
        description: `Document marked as ${label.toLowerCase()}.`,
      });
      // Clear rejection state if we just rejected
      if (variables.status === "rejected") {
        setRejectingDocId(null);
        setRejectionReason("");
      }
    },
    onError: () => {
      toast({
        title: "Update failed",
        description: "Could not update the document status. Please try again.",
        variant: "destructive",
      });
    },
  });

  // F008: Bulk status mutation
  const bulkStatusMutation = useMutation({
    mutationFn: ({
      documentIds,
      status,
    }: {
      documentIds: string[];
      status: string;
    }) =>
      apiRequest("PATCH", `/api/crm/deals/${dealId}/documents/bulk-status`, {
        documentIds,
        collectionStatus: status,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/crm/deals", dealId, "documents"],
      });
      const label = STATUS_LABELS[variables.status as DocumentCollectionStatus] ?? variables.status;
      const count = variables.documentIds.length;
      toast({
        title: "Bulk update complete",
        description: `${count} document${count !== 1 ? "s" : ""} marked as ${label.toLowerCase()}.`,
      });
      setSelectedIds(new Set());
    },
    onError: () => {
      toast({
        title: "Bulk update failed",
        description: "Could not update document statuses. Please try again.",
        variant: "destructive",
      });
    },
  });

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const totalCount = documents.length;
  const completeCount = documents.filter((d) =>
    ["received", "verified", "linked"].includes(d.collectionStatus)
  ).length;
  const progressPct = totalCount > 0 ? Math.round((completeCount / totalCount) * 100) : 0;

  // Apply status filter (progress bar always reflects total, not filtered)
  const filteredDocuments =
    statusFilter === "all"
      ? documents
      : documents.filter((d) => d.collectionStatus === statusFilter);

  const applicantGroups = groupByApplicant(filteredDocuments);

  // All visible document IDs (for Select All)
  const allVisibleIds = filteredDocuments.map((d) => d.id);
  const allSelected =
    allVisibleIds.length > 0 &&
    allVisibleIds.every((id) => selectedIds.has(id));
  const _someSelected = allVisibleIds.some((id) => selectedIds.has(id));

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  function handleDeleteClick(doc: DealDocument) {
    setDeleteDocId(doc.id);
    setDeleteDocName(doc.documentTypeName);
    setDeleteDialogOpen(true);
  }

  function confirmDelete() {
    if (deleteDocId) {
      deleteMutation.mutate(deleteDocId);
    }
  }

  function handleAddDocument() {
    setEditingDocument(null);
    setUploadModalOpen(true);
  }

  function handleEditDocument(doc: DealDocument) {
    setEditingDocument(doc);
    setUploadModalOpen(true);
  }

  // F008: Toggle single checkbox
  function toggleSelect(docId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  }

  // F008: Toggle all visible checkboxes (wired via Select All header checkbox)
  function _toggleSelectAll() {
    if (allSelected) {
      // Deselect all visible
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      // Select all visible
      setSelectedIds((prev) => {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.add(id));
        return next;
      });
    }
  }

  // F008: Quick status transition
  function handleStatusTransition(
    doc: DealDocument,
    newStatus: DocumentCollectionStatus
  ) {
    const now = new Date().toISOString();
    const extra: Record<string, unknown> = {};
    if (newStatus === "requested") extra.requestedAt = now;
    if (newStatus === "received") extra.receivedAt = now;
    if (newStatus === "verified") extra.verifiedAt = now;
    statusMutation.mutate({ docId: doc.id, status: newStatus, extra });
  }

  // F008: Open reject inline form
  function handleRejectClick(docId: string) {
    setRejectingDocId(docId);
    setRejectionReason("");
  }

  // F008: Confirm rejection
  function handleConfirmReject(docId: string) {
    statusMutation.mutate({
      docId,
      status: "rejected",
      extra: { rejectionReason: rejectionReason.trim() || null },
    });
  }

  // F008: Bulk action
  function handleBulkStatus(newStatus: DocumentCollectionStatus) {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    bulkStatusMutation.mutate({ documentIds: ids, status: newStatus });
  }

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center py-16"
        data-testid="documents-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Empty state
  // ---------------------------------------------------------------------------

  if (documents.length === 0) {
    return (
      <>
        <Card data-testid="documents-empty-state">
          <CardContent className="pt-6">
            <div className="text-center py-12 space-y-4">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto" />
              <div>
                <p className="text-sm font-medium text-foreground">
                  No documents in checklist
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Generate a checklist or add documents manually.
                </p>
              </div>
              <div className="flex items-center justify-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="button-generate-checklist"
                  onClick={() => setGenerateModalOpen(true)}
                >
                  <ListChecks className="h-4 w-4 mr-1" />
                  Generate Checklist
                </Button>
                <Button
                  size="sm"
                  data-testid="add-document-button-empty"
                  onClick={handleAddDocument}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Document
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload modal — available even in empty state */}
        <DocumentUploadModal
          open={uploadModalOpen}
          onOpenChange={setUploadModalOpen}
          mode="deal"
          dealId={dealId}
          existingDocument={null}
        />

        {/* Generate Checklist modal */}
        <GenerateChecklistModal
          open={generateModalOpen}
          onOpenChange={setGenerateModalOpen}
          dealId={dealId}
          onGenerated={() => {
            queryClient.invalidateQueries({
              queryKey: ["/api/crm/deals", dealId, "documents"],
            });
          }}
        />
      </>
    );
  }

  // ---------------------------------------------------------------------------
  // Main render — documents present
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4" data-testid="deal-documents-tab">
      {/* Header row: title + Generate Checklist + Add button */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          Document Checklist
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            data-testid="button-generate-checklist"
            onClick={() => setGenerateModalOpen(true)}
          >
            <ListChecks className="h-4 w-4 mr-1" />
            Generate Checklist
          </Button>
          <Button
            size="sm"
            data-testid="add-document-button"
            onClick={handleAddDocument}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Document
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <Card data-testid="documents-progress-card">
        <CardContent className="pt-4 pb-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Collection progress</span>
              <span data-testid="progress-fraction">
                {completeCount}/{totalCount} ({progressPct}%)
              </span>
            </div>
            <Progress
              value={progressPct}
              className="h-2"
              data-testid="documents-progress-bar"
            />
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Filter by status:</span>
        <Select
          value={statusFilter}
          onValueChange={setStatusFilter}
        >
          <SelectTrigger
            className="w-44 h-8 text-xs"
            data-testid="status-filter-trigger"
          >
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent data-testid="status-filter-content">
            {FILTER_OPTIONS.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                data-testid={`filter-option-${opt.value}`}
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* F008: Bulk action toolbar — shown when 1+ items selected */}
      {selectedIds.size > 0 && (
        <div
          className="flex items-center gap-3 px-3 py-2 bg-muted/50 rounded-md border"
          data-testid="bulk-action-toolbar"
        >
          <span
            className="text-sm font-medium"
            data-testid="bulk-selection-count"
          >
            {selectedIds.size} document{selectedIds.size !== 1 ? "s" : ""} selected
          </span>
          <div className="flex items-center gap-2 ml-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="bulk-mark-requested"
              disabled={bulkStatusMutation.isPending}
              onClick={() => handleBulkStatus("requested")}
            >
              Mark as Requested
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="bulk-mark-received"
              disabled={bulkStatusMutation.isPending}
              onClick={() => handleBulkStatus("received")}
            >
              Mark as Received
            </Button>
            <Button
              variant="ghost"
              size="sm"
              data-testid="bulk-clear-selection"
              onClick={() => setSelectedIds(new Set())}
            >
              <X className="h-4 w-4 mr-1" />
              Clear Selection
            </Button>
          </div>
          {bulkStatusMutation.isPending && (
            <Loader2 className="h-4 w-4 animate-spin ml-auto" />
          )}
        </div>
      )}

      {/* Applicant groups */}
      {applicantGroups.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p
              className="text-center text-sm text-muted-foreground py-6"
              data-testid="no-filtered-documents"
            >
              No documents match the selected filter.
            </p>
          </CardContent>
        </Card>
      ) : (
        applicantGroups.map((group) => (
          <Card key={group.key} data-testid={`applicant-group-${group.key}`}>
            <CardHeader className="py-3 px-4 border-b">
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className="text-xs uppercase font-semibold tracking-wide"
                  data-testid={`applicant-role-badge-${group.key}`}
                >
                  {group.applicantRole}
                </Badge>
                <span
                  className="text-sm font-medium"
                  data-testid={`applicant-name-${group.key}`}
                >
                  {group.applicantName ?? "Unknown Applicant"}
                </span>
                <span className="text-xs text-muted-foreground ml-auto">
                  {group.documents.length} document
                  {group.documents.length !== 1 ? "s" : ""}
                </span>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    {/* F008: Select All checkbox */}
                    <TableHead className="w-10 pl-4">
                      <Checkbox
                        checked={
                          allVisibleIds.length > 0 &&
                          group.documents.every((d) => selectedIds.has(d.id))
                            ? true
                            : group.documents.some((d) => selectedIds.has(d.id))
                            ? "indeterminate"
                            : false
                        }
                        onCheckedChange={() => {
                          const groupIds = group.documents.map((d) => d.id);
                          const allGroupSelected = groupIds.every((id) =>
                            selectedIds.has(id)
                          );
                          setSelectedIds((prev) => {
                            const next = new Set(prev);
                            if (allGroupSelected) {
                              groupIds.forEach((id) => next.delete(id));
                            } else {
                              groupIds.forEach((id) => next.add(id));
                            }
                            return next;
                          });
                        }}
                        aria-label="Select all documents in group"
                        data-testid={`select-all-group-${group.key}`}
                      />
                    </TableHead>
                    <TableHead className="pl-2">Document Type</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Received</TableHead>
                    <TableHead className="text-right pr-4">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.documents.map((doc) => (
                    <>
                      <TableRow key={doc.id} data-testid={`document-row-${doc.id}`}>
                        {/* F008: Row checkbox */}
                        <TableCell className="w-10 pl-4">
                          <Checkbox
                            checked={selectedIds.has(doc.id)}
                            onCheckedChange={() => toggleSelect(doc.id)}
                            aria-label={`Select ${doc.documentTypeName}`}
                            data-testid={`select-doc-${doc.id}`}
                          />
                        </TableCell>
                        <TableCell className="pl-2 font-medium text-sm">
                          <div className="flex items-center gap-1.5">
                            <span>{doc.documentTypeName}</span>
                            {/* F008: Expiry indicator */}
                            <ExpiryIndicator expiryDate={doc.expiryDate} />
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(doc.collectionStatus)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {doc.receivedAt
                            ? format(new Date(doc.receivedAt), "d MMM yyyy")
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right pr-2">
                          <div className="flex items-center justify-end gap-1 flex-wrap">
                            {/* F008: Quick status transition buttons */}
                            {doc.collectionStatus === "outstanding" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                data-testid={`request-document-${doc.id}`}
                                disabled={statusMutation.isPending}
                                onClick={() =>
                                  handleStatusTransition(doc, "requested")
                                }
                              >
                                Request
                              </Button>
                            )}
                            {doc.collectionStatus === "requested" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                data-testid={`receive-document-${doc.id}`}
                                disabled={statusMutation.isPending}
                                onClick={() =>
                                  handleStatusTransition(doc, "received")
                                }
                              >
                                Mark Received
                              </Button>
                            )}
                            {doc.collectionStatus === "received" && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                data-testid={`verify-document-${doc.id}`}
                                disabled={statusMutation.isPending}
                                onClick={() =>
                                  handleStatusTransition(doc, "verified")
                                }
                              >
                                <CheckSquare className="h-3.5 w-3.5 mr-1" />
                                Verify
                              </Button>
                            )}
                            {/* F008: Reject button — shown for non-rejected statuses */}
                            {doc.collectionStatus !== "rejected" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10"
                                data-testid={`reject-document-${doc.id}`}
                                disabled={statusMutation.isPending}
                                onClick={() => handleRejectClick(doc.id)}
                              >
                                Reject
                              </Button>
                            )}
                            {/* View / Edit / Delete */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              data-testid={`view-document-${doc.id}`}
                              onClick={() => {
                                if (doc.fileUrl) {
                                  window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
                                }
                              }}
                            >
                              <Eye className="h-4 w-4" />
                              <span className="sr-only">View</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                              data-testid={`edit-document-${doc.id}`}
                              onClick={() => handleEditDocument(doc)}
                            >
                              <Edit className="h-4 w-4" />
                              <span className="sr-only">Edit</span>
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                              data-testid={`delete-document-${doc.id}`}
                              onClick={() => handleDeleteClick(doc)}
                            >
                              <Trash2 className="h-4 w-4" />
                              <span className="sr-only">Delete</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>

                      {/* F008: Rejection reason inline form */}
                      {rejectingDocId === doc.id && (
                        <TableRow
                          key={`reject-${doc.id}`}
                          data-testid={`reject-form-row-${doc.id}`}
                        >
                          <TableCell colSpan={5} className="py-2 px-4 bg-destructive/5">
                            <div className="flex items-center gap-2">
                              <Input
                                placeholder="Enter rejection reason (optional)…"
                                value={rejectionReason}
                                onChange={(e) =>
                                  setRejectionReason(e.target.value)
                                }
                                className="h-8 text-xs flex-1"
                                data-testid={`rejection-reason-input-${doc.id}`}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") handleConfirmReject(doc.id);
                                  if (e.key === "Escape") {
                                    setRejectingDocId(null);
                                    setRejectionReason("");
                                  }
                                }}
                              />
                              <Button
                                variant="destructive"
                                size="sm"
                                className="h-8 text-xs"
                                data-testid={`confirm-reject-${doc.id}`}
                                disabled={statusMutation.isPending}
                                onClick={() => handleConfirmReject(doc.id)}
                              >
                                {statusMutation.isPending ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                                ) : null}
                                Confirm Reject
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 text-xs"
                                data-testid={`cancel-reject-${doc.id}`}
                                onClick={() => {
                                  setRejectingDocId(null);
                                  setRejectionReason("");
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ))
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
      >
        <AlertDialogContent data-testid="delete-document-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <strong>{deleteDocName}</strong> from the checklist? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-document-cancel"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-document-confirm"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Document upload / edit modal (F005) */}
      <DocumentUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        mode="deal"
        dealId={dealId}
        accountId={editingDocument?.accountId ?? undefined}
        contactId={editingDocument?.contactId ?? undefined}
        applicantRole={editingDocument?.applicantRole ?? undefined}
        existingDocument={editingDocument as Record<string, unknown> | null}
      />

      {/* F008: Generate Checklist modal */}
      <GenerateChecklistModal
        open={generateModalOpen}
        onOpenChange={setGenerateModalOpen}
        dealId={dealId}
        onGenerated={() => {
          queryClient.invalidateQueries({
            queryKey: ["/api/crm/deals", dealId, "documents"],
          });
        }}
      />
    </div>
  );
}
